import React, { lazy, Suspense } from 'react';
import './App.css';
import './index.css';
import "./markdown.css";
import {Routes, Route, BrowserRouter, Outlet} from "react-router-dom";
import AuthProvider, {AuthContext} from "./Contexts/AuthProvider";
import {TemplateProvider} from "./Contexts/TemplateContext";
import {NotificationProvider} from "./Contexts/NotificationProvider";
import {GoogleReCaptchaProvider} from '@google-recaptcha/react';
import {useContext} from "react";
import ToastCant from "./Components/ToastCant";
import LandingOut from "./Components/LandingOut";
import {ErrorBoundary} from "@/Components/ErrorBoundary.jsx";
import DashboardLayout from "@/Layouts/DashboardLayout.jsx";
import Spinner from "./Pages/Spinner";
import {Toaster} from "@/Components/ui/sonner.jsx";

// Lazy load all components
const Landing = lazy(() => import("./Pages/Landing"));
const Dashboard = lazy(() => import("./Components/Dashboard"));
const Chat = lazy(() => import("./Pages/Dashboard/Chat"));
const Assistants = lazy(() => import("./Pages/Services/Assistants.jsx"));
const Translator = lazy(() => import("./Pages/Dashboard/Translator"));
const Summarizer = lazy(() => import("./Pages/Dashboard/Summarizer.jsx"));
const ImageGenerator = lazy(() => import("./Pages/Services/ImagesGenerator"));
const Templates = lazy(() => import("./Pages/Templates"));
const Menu = lazy(() => import("./Components/Menu"));
const Overview = lazy(() => import("./Pages/Dashboard/Overview.jsx"));
const Login = lazy(() => import("./Pages/User/Login"));
const LoggedInDevices = lazy(() => import("./Pages/User/Settings/Pages/LoggedInDevices"));
const Settings = lazy(() => import("./Pages/User/Settings/Settings"));
const CreateTemplate = lazy(() => import("./Pages/CreateTemplate"));
const TemplateDetail = lazy(() => import("./Pages/Services/TemplateDetail"));
const AudioGenerator = lazy(() => import("./Pages/Dashboard/AudioGenerator.jsx"));
const Register = lazy(() => import("./Pages/User/Register"));
const Bot = lazy(() => import("./Pages/Services/Bot/Bot"));
const BotDetail = lazy(() => import("./Pages/Services/Bot/Pages/BotDetail"));
const UserDataRelations = lazy(() => import("./Pages/User/Settings/UserDataRelations"));
const ThreadsHistory = lazy(() => import("./Pages/User/ThreadsHistory"));
const AffiliateDashboard = lazy(() => import("./Pages/AffiliateDashboard"));
const PricingComponent = lazy(() => import("./Pages/Pricing"));
const ThankYou = lazy(() => import("./Pages/ThankYou"));
const AIChat = lazy(() => import("./Pages/User/AIChat"));
const AboutPage = lazy(() => import("./Pages/AboutPage.jsx"));
const PricingTrialComponent = lazy(() => import("./Pages/PricingTrial"));
const BlogPage = lazy(() => import("./Pages/BlogPage"));
const BlogPostDetail = lazy(() => import("./Pages/BlogPostDetail"));
const ContactPage = lazy(() => import("./Pages/ContactPage"));
const TermsOfService = lazy(() => import("./Pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./Pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./Pages/RefundPolicy"));
const Cookies = lazy(() => import("./Pages/Cookies"));
const DiscordAuthHandler = lazy(() => import("./Pages/DiscordAuthHandler"));
const PasswordReset = lazy(() => import("./Pages/User/PasswordReset"));
const Audio = lazy(() => import("./Pages/Audio"));
const AudioVoiceClone = lazy(() => import("./Pages/Dashboard/AudioVoiceClone"));
const SpeechToText = lazy(() => import("./Pages/Dashboard/SpeechToText.jsx"));
const SoundEffects = lazy(() => import("./Pages/Dashboard/SoundEffects.jsx"));
const AudioIsolation = lazy(() => import("./Pages/Dashboard/AudioIsolation"));
const DubbingService = lazy(() => import("./Pages/Services/DubbingService"));
const StudentSection = lazy(() => import("./Pages/User/StudentSection"));
const FreelancerSection = lazy(() => import("./Pages/User/FreelancerSection"));
const AiDocumentEditor = lazy(() => import("./Pages/Services/AiDocumentEditor.jsx"));
const DocumentList = lazy(() => import("./Pages/User/DocumentList"));
const Gallery = lazy(() => import("./Pages/Gallery"));
const ImagePostModal = lazy(() => import("./Components/ImagesPostModal"));
const ImagePost = lazy(() => import("./Components/ImagesPost"));
const ProfilePage = lazy(() => import("./Pages/ProfilePage"));
const VoteAIArtPage = lazy(() => import("./Pages/VoteAIArtPage"));
const SpotifyCallback = lazy(() => import("./Components/SpotifyCallback"));
const SpotifyLink = lazy(() => import("./Components/SpotifyLink"));
const MermaidDiagram = lazy(() => import("./Pages/Services/MermaidDiagram"));
const ImageEditor = lazy(() => import("./Pages/Services/ImageEditor"));
const ImageDescriber = lazy(() => import("./Pages/Services/ImageDescriber"));
const CaloriesDashboard = lazy(() => import("./Pages/Dashboard/CaloriesDashboard"));
const ImageRemovebg = lazy(() => import("./Pages/Services/ImageRemovebg"));
const ImageModelCards = lazy(() => import("./Pages/User/ImageModelCards"));
const SpecialOffer = lazy(() => import("./Pages/SpecialOffer"));
const Assistents = lazy(() => import("@/Pages/Dashboard/Assistents.jsx"));
const AssistentDetail = lazy(() => import("@/Pages/Dashboard/AssistentDetail.jsx"));
const UserFiles = lazy(() => import("@/Pages/Dashboard/UserFiles.jsx"));
const CreditsHistory = lazy(() => import("@/Pages/Dashboard/CreditsHistory.jsx"));
const SpecialOfferSK = lazy(() => import("@/Pages/SpecialOfferSK.jsx"));

// Layout component to wrap the nested routes with the Menu
function MenuLayout() {
    return (
        <Menu>
            <Outlet/>
        </Menu>
    );
}

function LandingLayout() {
    return (
        <LandingOut>
            <Outlet/>
        </LandingOut>
    );
}

function App() {


    return (
        <BrowserRouter>
            <AuthProvider>
                <NotificationProvider>
                    <TemplateProvider>
                        <div className="dark:bg-black bg-white flex flex-col min-h-screen">
                            <div
                                className="fixed top-4 right-4 flex flex-col-reverse items-end space-y-4 pb-4 z-[9999]">
                            </div>
                            <div className=" flex flex-col items-center">
                                <div className="w-full h-full">
                                    <Suspense fallback={<Spinner />}>
                                        <Routes>
                                        {/* Public Routes */}
                                        <Route element={<LandingLayout/>}>
                                            <Route path="/" element={<Landing title="Overview"/>}/>
                                            <Route path="/unsubscribe" element={<Landing title="Overview"/>}/>
                                            <Route path="/login" element={<ErrorBoundary><Login/></ErrorBoundary>}/>
                                            <Route path="/password-reset" element={<PasswordReset/>}/>
                                            <Route path="/register" element={<Register/>}/>
                                            <Route path="/about" element={<AboutPage/>}/>
                                            <Route path="/ai-chat" element={<AIChat/>}/>
                                            <Route path="/pricing-trial" element={<PricingTrialComponent/>}/>
                                            <Route path="/pricing-trial/:id" element={<PricingTrialComponent/>}/>
                                            <Route path="/blog" element={<BlogPage/>}/>
                                            <Route path="/social-post" element={<BlogPage/>}/>
                                            <Route path="/blog/:id" element={<BlogPostDetail/>}/>
                                            <Route path="/contact" element={<ContactPage/>}/>
                                            <Route path="/tos" element={<TermsOfService/>}/>
                                            <Route path="/gdpr" element={<PrivacyPolicy/>}/>
                                            <Route path="/refund-policy" element={<RefundPolicy/>}/>
                                            <Route path="/cookies" element={<Cookies/>}/>
                                            <Route path="/discord-auth" element={<DiscordAuthHandler/>}/>
                                            <Route path="/student-section" element={<StudentSection/>}/>
                                            <Route path="/freelancer-section" element={<FreelancerSection/>}/>
                                            <Route path="/gallery" element={<Gallery/>}/>
                                            <Route path="/profile" element={<ProfilePage/>}/>
                                            <Route path="/profile/:username" element={<ProfilePage/>}/>
                                            <Route path="/hlasuj" element={<VoteAIArtPage/>}/>
                                            <Route path="/spotify" element={<SpotifyCallback/>}/>
                                            <Route path="/specialni-nabidka" element={<SpecialOffer/>}/>
                                            <Route path="/specialna-ponuka" element={<SpecialOfferSK/>}/>
                                        </Route>

                                        {/* Protected / Nested Routes wrapped in Menu layout */}
                                        <Route element={<DashboardLayout/>}>
                                            <Route path="/dashboard" element={<Dashboard/>}/>
                                            <Route path="/chat" element={<Chat/>}/>
                                            <Route path="/chat/:id" element={<Chat/>}/>
                                            <Route path="/assistants" element={<Assistants/>}/>
                                            <Route path="/translate" element={<Translator/>}/>
                                            <Route path="/summarizer" element={<Summarizer/>}/>
                                            <Route path="/images" element={<ImageGenerator/>}/>
                                            <Route path="/audio/voice-cloning" element={<AudioVoiceClone/>}/>
                                            <Route path="/audio/speech-to-text" element={<SpeechToText/>}/>
                                            <Route path="/audio/sound-effects" element={<SoundEffects/>}/>
                                            <Route path="/audio/voice-isolation" element={<AudioIsolation/>}/>
                                            <Route path="/audio/dubbing" element={<DubbingService/>}/>
                                            <Route path="/text-speech" element={<AudioGenerator/>}/>
                                            <Route path="/edit-image" element={<ImageEditor/>}/>
                                            <Route path="/removebg" element={<ImageRemovebg/>}/>
                                            <Route path="/describe" element={<ImageDescriber/>}/>
                                            <Route path="/templates" element={<Assistents/>}/>
                                            <Route path="/overview" element={<Overview/>}/>
                                            <Route path="/sessions" element={<LoggedInDevices/>}/>
                                            <Route path="/settings" element={<Settings/>}/>
                                            <Route path="/spinner" element={<Spinner/>}/>
                                            <Route path="/create-template" element={<CreateTemplate/>}/>
                                            <Route path="/template/:templateId" element={<AssistentDetail/>}/>
                                            <Route path="/bot" element={<Bot/>}/>
                                            <Route path="/bot-detail" element={<BotDetail/>}/>
                                            <Route path="/data" element={<UserFiles/>}/>
                                            <Route path="/history" element={<CreditsHistory/>}/>
                                            <Route path="/affiliate" element={<AffiliateDashboard/>}/>
                                            <Route path="/pricing" element={<PricingComponent/>}/>
                                            <Route path="/purchased" element={<ThankYou/>}/>
                                            <Route path="/documents/edit" element={<AiDocumentEditor/>}/>
                                            <Route path="/documents/edit/:documentId" element={<AiDocumentEditor/>}/>
                                            <Route path="/documents" element={<DocumentList/>}/>
                                            <Route path="/post" element={<ImagePost/>}/>
                                            <Route path="/mindmap/:uuid" element={<MermaidDiagram/>}/>
                                            <Route path="/graphic" element={<ImageModelCards/>}/>
                                            <Route path="/calories" element={<CaloriesDashboard/>}/>
                                        </Route>
                                        </Routes>
                                    </Suspense>
                                </div>
                            </div>
                            <Toaster position="top-center" />
                        </div>
                    </TemplateProvider>
                </NotificationProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
